import { Injectable } from '@angular/core';
import {
    BackendOberflaecheService,
    Helper,
    IRateService,
    OberflaecheElement,
    RateService,
    TarifierungProvider,
    TarifierungResponse,
} from '@vhv/tarifrechner-common';
import { RateConfigService } from './rate-config.service';

@Injectable({
    providedIn: 'root',
})
export class RateCyberService implements IRateService {

    private cachedRate: TarifierungResponse;

    private isDirektionsanfrage: boolean = false;

    private rateChangeCallbackFn: Function;

    constructor(
        private rateService: RateService,
        private rateConfigService: RateConfigService,
        private tarifierungProvider: TarifierungProvider,
        private backendOberflaecheService: BackendOberflaecheService,
    ) {
        this.rateService.setRateService(this);
        this.backendOberflaecheService.getOberflaeche()
            .then((value: any) => {
                this.backendOberflaecheService
                    .getElementByName('IstDirektionsanfrage')
                    .registerObservable()
                    .subscribe(this.direktionsanfrageObserverFn);
            })
            .catch((err: any) => {
                console.error('Oberflaeche konnte fuer Cyber-Rate-Service nicht gelesen werden', err);
            });
    }

    // Funktion, die vom Element gerufen wird, wenn es sich geaendert hat.
    direktionsanfrageObserverFn = (element: OberflaecheElement): void => {
        // uebergebene Element pruefen und Direktionsanfrage merken und dann die callBack-Funktion
        // aufrufen. Diese aendert dann die Sidebar.
        if (this.backendOberflaecheService.getElementByName('IstDirektionsanfrage').value === 'true') {
            this.isDirektionsanfrage = true;
            this.callRateChangedCallbackFn(null, this.isDirektionsanfrage);
        } else {
            this.isDirektionsanfrage = false;
            if (!this.cachedRate) {
                this.calculateBeitrag();
            } else {
                this.callRateChangedCallbackFn(this.cachedRate, this.isDirektionsanfrage);
            }
        }
    };

    // Zentrale Funktion, um den Callback auszufuehren und hier
    // nochmals auf Direktionsanfrage zu pruefen, um parallele
    // Requests einzufangen.
    callRateChangedCallbackFn(value: TarifierungResponse, isDirektionsanfrage: boolean): void {
        if (isDirektionsanfrage) {
            this.rateChangeCallbackFn(null, isDirektionsanfrage);
        } else {
            this.rateChangeCallbackFn(value, isDirektionsanfrage);
        }
    }

    getCachedBeitrag(): TarifierungResponse {
        if (this.backendOberflaecheService.getElementByName('IstDirektionsanfrage').value === 'true') {
            return null;
        }
        return this.cachedRate;
    }

    getCachedBausteinBeitrag(bausteinName: string): TarifierungResponse {
        //TODO
        return null;
    }

    public registerCallback(callbackFn: Function): void {
        this.rateChangeCallbackFn = callbackFn;
    }

    calculateBeitrag(): void {
        if (this.isDirektionsanfrage) {
            this.callRateChangedCallbackFn(null, this.isDirektionsanfrage);
        } else {
            this.getBeitrag().then(value => {
                this.callRateChangedCallbackFn(value, this.isDirektionsanfrage);
            }).catch(error => {
                console.debug('Beitrag kann nicht ermittelt werden: ' + error);
            });
        }
    }

    getBausteinBeitrag(bausteinName: string): Promise<TarifierungResponse> {
        if (this.isDirektionsanfrage) {
            this.callRateChangedCallbackFn(null, this.isDirektionsanfrage);
            return null;
        } else {
            return this.tarifierungProvider.getTarifierungBeitragP(
                'test',
                'JAEHRLICH',
                '',
                this.getBeitragCyberFachdaten(true),
            );
        }
    }


    public getBeitrag(): Promise<TarifierungResponse> {
        if (this.isDirektionsanfrage) {
            this.callRateChangedCallbackFn(null, this.isDirektionsanfrage);
            return new Promise<TarifierungResponse>(resolve => {
                resolve(null);
            });
        } else {
            return this.tarifierungProvider.getTarifierungBeitragP(
                'test',
                'JAEHRLICH',
                '',
                this.getBeitragCyberFachdaten(),
            );
        }
    }

    public getFachdaten(): any {
        return this.getCyberFachdaten();
    }

    public getCyberFachdaten(alleBausteine?: boolean): CyberFachdaten {
        const ausgefuellteWerte: CyberTarifrechnerDaten = this.backendOberflaecheService.getAusgefuellteWerte() as CyberTarifrechnerDaten;

        return {
            azvm: ausgefuellteWerte.Azvm,
            sitzMutterkonzern: ausgefuellteWerte.SitzMutterkonzern,
            betriebsart: ausgefuellteWerte.Betriebsart,
            jahresumsatz: (+ausgefuellteWerte.Jahresumsatz) * 1000, // Der Jahresumsatz enthaelt nicht die Addon-Zeichen '.000 €', somit muss noch mit 1000 multipliziert werden.
            bausteinEPayment: alleBausteine ? true : Helper.boolify(ausgefuellteWerte.BausteinEPayment),
            bausteinSpionage: alleBausteine ? true : Helper.boolify(ausgefuellteWerte.BausteinSpionage),
            bausteinVertrauensschaden: alleBausteine ? true : Helper.boolify(ausgefuellteWerte.BausteinVertrauensschaden),
            bausteinBetriebsunterbrechungCloud: alleBausteine ? true : Helper.boolify(ausgefuellteWerte.BausteinBetriebsunterbrechungCloud),
            bausteinBetriebsunterbrechungSystemausfall: alleBausteine ? true : Helper.boolify(ausgefuellteWerte.BausteinBetriebsunterbrechungSystemausfall),
            monetaererSelbstbehalt: ausgefuellteWerte.MonetaererSelbstbehalt,
            zeitlicherSelbstbehalt: ausgefuellteWerte.ZeitlicherSelbstbehalt,
            versicherungssumme: ausgefuellteWerte.Versicherungssumme,
            onlineshop: Helper.boolify(ausgefuellteWerte.Onlineshop),
            onlineshopUmsatz: Helper.boolify(ausgefuellteWerte.OnlineshopUmsatz),
            passwortrichtlinie: ausgefuellteWerte.Passwortrichtlinie,
            systemadministratoren: Helper.boolify(ausgefuellteWerte.Systemadministratoren),
            datensicherung: ausgefuellteWerte.Datensicherung,
            datenwiederherstellung: ausgefuellteWerte.Datenwiederherstellung,
            sicherheitsupdates: ausgefuellteWerte.Sicherheitsupdates,
            hackerangriff: Helper.boolify(ausgefuellteWerte.Hackerangriff),
            hackerangriffBeschreibung: ausgefuellteWerte.HackerangriffBeschreibung,
            verdachtHackerangriff: Helper.boolify(ausgefuellteWerte.VerdachtHackerangriff),
            verdachtHackerangriffBeschreibung: ausgefuellteWerte.VerdachtHackerangriffBeschreibung,
            hatVorversicherung: Helper.boolify(ausgefuellteWerte.HatVorversicherung),
            vorversicherer: ausgefuellteWerte.Vorversicherer,
            vorversicherungGekuendigt: ausgefuellteWerte.VorversicherungGekuendigt,
            downloadZeitstempel: ausgefuellteWerte.DownloadZeitstempel,
        };


    }

    private getBeitragCyberFachdaten(alleBausteine?: boolean): CyberFachdaten {
        const tarifierungsdaten = this.getCyberFachdaten(alleBausteine);

        // Das folgenden Felder müssen für die Validatoren ausgefüllt sein, fülle mit Dummy-Wert für Beitragsberechnung
        tarifierungsdaten.passwortrichtlinie = 'n/a';
        tarifierungsdaten.datensicherung = 'n/a';
        tarifierungsdaten.datenwiederherstellung = 'n/a';
        tarifierungsdaten.sicherheitsupdates = 'n/a';
        tarifierungsdaten.downloadZeitstempel = 'n/a';

        return tarifierungsdaten;
    }

    private handleVHV(
        rateConfigService: RateConfigService,
        tarifierungProvider: TarifierungProvider,
    ): void {
        this.getBeitrag()
            .then(value => {
                const beitrag = value.bruttoBeitragGemZahlweise;
                console.debug(beitrag, this);
            })
            .catch(error => {
                console.error(error);
            });
    }
}
